import { Component, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { WorkTaskService } from 'src/app/services/work-task.service';
import { FormsService } from 'src/app/services/forms.service';
import { ToastService } from 'src/app/services/toast.service';
import { ReportModel } from 'src/app/models/report.model';
import { DatePipe } from '@angular/common';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatDivider } from '@angular/material/list';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-report-ao',
  templateUrl: './report-ao.component.html',
  styleUrls: ['./report-ao.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, MatCheckbox, MatButton, MatProgressSpinner]
})
export class ReportAOComponent {
  private readonly workTaskSvc = inject(WorkTaskService);
  toastr = inject(ToastService);
  datePipe = inject(DatePipe);
  private readonly formsSrv = inject(FormsService);

  reportForm = this.createReport();

  filter = new UntypedFormGroup({
    fromDate: new UntypedFormControl(''),
    toDate: new UntypedFormControl(''),
  });

  grouping: any[] = [];
  selectedFilterValue: any = null;
  finishedEmitEvent: boolean = false;
  selectedfilterId = new UntypedFormGroup({
    id: new UntypedFormControl(null),
  });

  checkboxValue: boolean = false;
  valorEntero: number = 0;
  isLoading = false;

  createReport(): UntypedFormGroup {
    return new UntypedFormGroup({
      groupType: new UntypedFormControl([]),
      taskType: new UntypedFormControl([]),
      fromDate: new UntypedFormControl('', Validators.required),
      toDate: new UntypedFormControl('', Validators.required),
      taskUnassigned: new UntypedFormControl({ value: false, disabled: false }),
    });
  }

  checkSelectedFilterValue() {
    if (this.selectedFilterValue && this.finishedEmitEvent) {
      this.selectedFilterValue = null;
      this.finishedEmitEvent = false;
      this.clearSelectedfilterId();
    }
  }

  clearSelectedfilterId() {
    this.selectedfilterId.controls.id.setValue(null);
    this.selectedFilterValue = null;
  }

  onCheckboxChange(event: MatCheckboxChange) {
    this.valorEntero = event.checked ? 1 : 0;
    if (event.checked) {
      this.reportForm.controls['fromDate'].setValidators([]);
      this.reportForm.controls['toDate'].setValidators([]);
    } else {
      this.reportForm.controls['fromDate'].setValidators(Validators.required);
      this.reportForm.controls['toDate'].setValidators(Validators.required);
    }
    this.reportForm.controls['fromDate'].updateValueAndValidity();
    this.reportForm.controls['toDate'].updateValueAndValidity();
  }

  formatDateLocal(d: Date, startOfDay: boolean) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = startOfDay ? '00' : '23';
    const mm = startOfDay ? '00' : '59';
    const ss = startOfDay ? '00' : '59';
    return `${y}-${m}-${day}T${hh}:${mm}:${ss}`;
  }
  async onSubmit() {
    this.reportForm.markAllAsTouched();
    if (this.reportForm.invalid) {
      this.toastr.error('Error', 'Complete los campos requeridos.');
      return;
    }

    // With "Incluir tareas sin agendar" the dates are optional: an empty date is sent as no filter instead of being
    // turned into an Invalid Date, which makes the DatePipe throw and the download silently do nothing.
    const fromDate = this.parseDate(this.reportForm.value.fromDate);
    const toDate = this.parseDate(this.reportForm.value.toDate);

    if (fromDate && toDate && toDate < fromDate) {
      this.toastr.error('Error', 'La fecha seleccionada no es correcta.');
      return;
    }

    fromDate?.setHours(0, 0, 0);
    toDate?.setHours(23, 59, 59);

    const fromDateStr = fromDate ? this.datePipe.transform(fromDate, 'yyyy-MM-ddTHH:mm:ss') : null;
    const toDateStr = toDate ? this.datePipe.transform(toDate, 'yyyy-MM-ddTHH:mm:ss') : null;

    const report: ReportModel = {
      taskGroupId: this.reportForm.value.groupType,
      taskJobId: this.reportForm.value.taskType,
      fromDate: fromDateStr,
      toDate: toDateStr,
      ShowNonScheduledTasks: this.reportForm.value.taskUnassigned,
    };

    this.isLoading = true;

    try {
      const response = await firstValueFrom(this.formsSrv.getAOReportCSV(report));
      const safeCsv = this.sanitizeCsvContent(response);

      const blob = new Blob([safeCsv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;

      const filename = this.datePipe.transform(Date.now(), 'ddMMyyyyHHmmss');
      a.setAttribute('download', filename + '.csv');

      a.click();
      URL.revokeObjectURL(url);
    } catch {
      this.toastr.error('Error', 'No se pudo descargar el archivo.');
    } finally {
      this.isLoading = false;
    }
  }

  private parseDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value as string | number | Date);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private sanitizeCsvContent(content: string): string {
    return content
      .replaceAll('%', encodeURIComponent('%'))
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }
}
